import React, { ReactNode, useReducer, useState } from 'react';
import userAwarenessReducer, {
  initialState,
} from '@zola-helpers/client/dist/es/redux/userAwareness/userAwarenessReducer';

// Types
import type { GenericOnboardingApiCall } from '@zola/zola-ui/src/components/OnboardV2/types';
import type { OnboardStepProps } from '@zola/zola-ui/src/components/OnboardV2/OnboardStep';
import type { OnboardingRequest } from '@zola-helpers/client/dist/es/api/onboard';

// Components
import UserAwarenessStep from '@zola/zola-ui/src/components/OnboardV2/common/steps/UserAwarenessStep';
import OnboardSection from '@zola/zola-ui/src/components/OnboardV2/OnboardSection';
import RegistryProductStep from 'components/onboard/onboard-v2/steps/RegistryProductStep';

// Util
import { BusinessUnitComponent } from '@zola-helpers/client/dist/types/@types';
import { BusinessUnit } from '@zola-helpers/client/dist/es/@types';
import { useOnboardingContext } from '@zola/zola-ui/src/components/OnboardV2/context';
import experimentFlags, { ONBOARDING_REGISTRY_VALUABLE } from 'util/experimentFlags';

type FinishUpSectionProps = Pick<OnboardStepProps, 'hed' | 'dek'> & {
  portalContent?: ReactNode;
  showStepCount?: boolean;
  onFormSubmit: (values: OnboardingRequest) => Promise<unknown>;
  hed?: OnboardStepProps['hed'];
  dek?: OnboardStepProps['dek'];
};

const FinishUpSection = ({
  showStepCount,
  onFormSubmit,
  portalContent,
  hed,
  dek,
}: FinishUpSectionProps) => {
  const [state, dispatch] = useReducer(userAwarenessReducer, initialState);
  const { onboardingState } = useOnboardingContext();
  const { currentUserContext } = onboardingState;
  const isExistingUser = !currentUserContext.is_guest;

  const [selectedHref, setSelectedHref] = useState('');
  const isInExperiment =
    window.location.pathname === '/wedding/onboard/registry' &&
    experimentFlags.isUnderTest(ONBOARDING_REGISTRY_VALUABLE, currentUserContext);

  const onSelectHref = (href: string) => setSelectedHref(href);

  const onSubmit = (values: {
    user_awareness_channel_other_text?: string;
    business_unit_feature: BusinessUnitComponent;
    business_unit_source: BusinessUnit;
  }): Promise<unknown> => {
    const { user_awareness_channel_other_text, business_unit_feature } = values;

    const { randomAwarenessChannelsOrder, awarenessChannels } = state;
    const user_awareness_channels = randomAwarenessChannelsOrder.filter(
      key => awarenessChannels[key]
    );

    return onFormSubmit({
      existing_account: isExistingUser,
      user_awareness_channels,
      user_awareness_channel_other_text,
      business_unit_feature,
    }).then(() => {
      if (isInExperiment) window.location.assign(selectedHref);
    });
  };

  const steps = isInExperiment ? (
    [
      <RegistryProductStep selectedHref={selectedHref} onSelectHref={onSelectHref} />,
      <UserAwarenessStep
        state={state}
        dispatch={dispatch}
        portalContent={portalContent}
        hed={hed}
        dek={dek}
      />,
    ]
  ) : (
    <UserAwarenessStep
      state={state}
      dispatch={dispatch}
      portalContent={portalContent}
      hed={hed}
      dek={dek}
    />
  );

  return (
    <OnboardSection
      showStepCount={showStepCount}
      onFormSubmit={(onSubmit as unknown) as GenericOnboardingApiCall}
    >
      {steps}
    </OnboardSection>
  );
};

export default FinishUpSection;
